/**
 * ZoomPanControls — the in-frame zoom/pan control cluster for the evaluation
 * workspace map stage (spec §3.3). Rendered INSIDE a react-zoom-pan-pinch
 * <TransformWrapper> by FloorBoundaryTracer when `enableZoom` is true, so it can
 * drive the transform via the library's `useControls()` hook.
 *
 * Controls: ＋ / − · live % readout · Fit-to-frame · hand/pan toggle.
 * Zoom-to-cursor on wheel/pinch + min/max clamp are configured on the
 * <TransformWrapper> itself (in FloorBoundaryTracer); this is the on-screen UI.
 *
 * TOKENS ONLY — every colour/space/radius is a design-system token
 * (src/styles/evaluate-workspace.css). No hardcoded hex.
 *
 * A11y: every control is a real <button> with an aria-label; the pan toggle
 * carries aria-pressed; the readout is aria-hidden (decorative — the buttons
 * carry the semantics). Hit targets ≥38px (desktop floor, DS §2.1).
 */
import { useControls } from 'react-zoom-pan-pinch';

export default function ZoomPanControls({
  scalePct,
  panMode,
  onTogglePan,
}: {
  /** live zoom percentage (rounded), lifted from the transform state. */
  scalePct: number;
  /** hand/pan tool active → a plain drag pans instead of marking. */
  panMode: boolean;
  onTogglePan: () => void;
}) {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <>
      {/* zoom % readout — top-left of the stage */}
      <div className="evalws-zoom-readout" aria-hidden="true">
        <span className="evalws-dot" />
        {scalePct}%
      </div>

      {/* screen-reader zoom feedback (Vera NIT #4) — the visual readouts are
          aria-hidden and the ± buttons don't carry the new level, so announce it
          politely here. Visually hidden; driven by the live scalePct. */}
      <span
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Zoom {scalePct}%
      </span>

      {/* control cluster — bottom-right of the stage */}
      <div className="evalws-zoom-cluster" role="group" aria-label="Zoom and pan controls">
        <button
          type="button"
          className="evalws-zc-fit"
          onClick={() => resetTransform()}
          aria-label="Fit plan to frame"
        >
          <svg className="evalws-ic" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9V4h5" />
            <path d="M20 9V4h-5" />
            <path d="M4 15v5h5" />
            <path d="M20 15v5h-5" />
          </svg>
          Fit to frame
        </button>

        <div className="evalws-zc-group">
          <button
            type="button"
            className="evalws-zc-btn"
            onClick={() => zoomIn()}
            aria-label="Zoom in"
          >
            <svg className="evalws-ic" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
          </button>
          <div className="evalws-zc-read" aria-hidden="true">{scalePct}%</div>
          <button
            type="button"
            className="evalws-zc-btn"
            onClick={() => zoomOut()}
            aria-label="Zoom out"
          >
            <svg className="evalws-ic" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12h14" />
            </svg>
          </button>
          <button
            type="button"
            className="evalws-zc-btn"
            onClick={onTogglePan}
            aria-pressed={panMode}
            aria-label={panMode ? 'Pan tool on — turn off to mark' : 'Pan tool off — turn on to drag the plan'}
            title="Hand / pan tool (or hold Space to pan momentarily)"
          >
            <svg className="evalws-ic" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12" />
              <path d="M11 11.5V4.5a1.5 1.5 0 0 1 3 0V12" />
              <path d="M14 11V6a1.5 1.5 0 0 1 3 0v8a6 6 0 0 1-6 6h-1a6 6 0 0 1-5.2-3l-2.3-4a1.5 1.5 0 0 1 2.6-1.5L8 14" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}
